import React from 'react';
import fm from 'format-message';
import localStorage from '@activebrands/core-web/libs/local-storage';
import { InfoBox } from '@activebrands/core-web/libs/storyblok/storyblok';
import { LooseObject } from '@activebrands/core-web/types/looseObject';
import parsePhoneNumberForPhoneInput from '@activebrands/core-web/utils/parse-phone-number-for-phone-input';
import { CountryCode } from 'libphonenumber-js';
import metadata from 'libphonenumber-js/metadata.min.json';
import { Countries } from 'state/ecommerce/types';
import { Country } from 'types/application';
import { User } from 'types/auth';
import { Address } from 'types/basket';

const NEWSLETTER_SIGNUP_LOCAL_STORAGE = process.env.REACT_APP_NEWSLETTER_SIGNUP_LOCAL_STORAGE;
interface InputFieldProps {
    ariaLabel?: string;
    $handleChange?: (event: React.ChangeEvent<HTMLInputElement>) => void; // This is an additional function that will be triggered onChange and after validation
    $style?: Record<string, any>;
    addSearchFilter?: boolean;
    autoComplete?: string;
    dataLocale?: string;
    defaultValue?: any;
    disabled?: boolean;
    label: string | Record<string, any>; // storyblok 'doc' type
    maxLength?: number;
    name: string;
    options?: any[];
    prefix?: string;
    required: boolean;
    type: 'email' | 'tel' | 'text' | 'select' | 'checkbox' | 'radio' | 'numeric' | 'hidden';
    userCountry?: string;
    validate?: string;
}

interface InfoBoxProps {
    infoBoxes: Array<InfoBox>;
    type: 'render';
}

interface CustomRenderProps extends LooseObject {
    type: 'render';
}

type AddressFormFieldsProps = {
    brandSpecific: {
        $styles: {
            address: {
                firstName: Record<string, any>;
                lastName: Record<string, any>;
            };
            shippingAddress: {
                firstName: Record<string, any>;
                lastName: Record<string, any>;
                shippingAddress: Record<string, any>;
            };
        };
        components: {
            InfoBoxes: React.Component;
            Heading: React.Component;
        };
        /*
         * This blacklist is for countries that doesn't have a Zip Code.
         * The blacklisted countries will not have zipcode required.
         * However, if a user is logged in and have set a zip code on their account,
         * and choose a country that doesn't have a zip code and leaves the zip code field empty.
         * The zip code from that user will anyway be sent to centra even if left empty.
         */
        zipCodeBlackList: string[];
    };
    checkboxData: {
        addressNewsletterText: Record<string, any>; // storyblok 'doc' type
        addressSeprateShippingText: Record<string, any>; // storyblok 'doc' type
        addressTermsText: Record<string, any>; // storyblok 'doc' type
        separateBillingAndShippingAddress: boolean;
        toggleSeparateShippingAndBilling: (value: React.ChangeEvent<HTMLInputElement>) => void;
    };
    countries: Countries;
    data: {
        address: Partial<Address>;
        shippingAddress?: Partial<Address>;
    };
    handleSelectedCountry: (value: string) => void;
    infoBoxData: {
        button: any;
        heading: string;
        paragraph: any;
        show: boolean;
    };
    selectedCountry: Country;
    shippingMarkets: string[];
    user?: User;
    userCountry?: Country;
};

export const addressFormFields = ({
    brandSpecific: {
        components: { InfoBoxes, Heading },
        $styles,
        zipCodeBlackList = ['HK', 'AE'],
    },
    checkboxData,
    countries,
    data,
    handleSelectedCountry,
    infoBoxData,
    selectedCountry,
    shippingMarkets,
    user,
    userCountry,
}: AddressFormFieldsProps) => {
    const newsletterSignup: boolean = NEWSLETTER_SIGNUP_LOCAL_STORAGE
        ? localStorage.get(NEWSLETTER_SIGNUP_LOCAL_STORAGE, false)
        : false;

    const availableStates = Object.keys(selectedCountry?.states || {});

    // we currently don't support setting user "state" in Account Settings, but for the future.
    const selectedState =
        availableStates.length > 0
            ? availableStates?.find(value => data.address.state === value || user?.state === value)
            : '';

    // Validate country code from user
    const parseUserCountryCode = (countryCode: string | undefined): CountryCode | undefined => {
        if (countryCode && metadata && Object.keys(metadata.countries).includes(countryCode as CountryCode)) {
            return countryCode as CountryCode;
        }
        // Return undefined if the country code is not valid
        return undefined;
    };

    const parsedAddressPhoneNumberData = parsePhoneNumberForPhoneInput({
        defaultCountryCode: (parseUserCountryCode(user?.country) ||
            selectedCountry?.id ||
            userCountry?.id) as CountryCode,
        phoneNumber: data.address.phoneNumber || user?.mobilePhone,
    });

    const parsedShippingPhoneNumberData = parsePhoneNumberForPhoneInput({
        defaultCountryCode: (selectedCountry?.id || userCountry?.id) as CountryCode,
        phoneNumber: data.shippingAddress?.phoneNumber,
    });

    const stateOptions = selectedCountry?.states
        ? Object.keys(selectedCountry.states).map(stateId => ({
              value: stateId,
              label: selectedCountry.states?.[stateId],
          }))
        : [];

    // Map countries to options for select list
    const countryOptions = shippingMarkets?.map(countryCode => ({
        value: countries[countryCode].id,
        label: countries[countryCode].name,
        icon: `https://flagcdn.com/h20/${countryCode.toLowerCase()}.png`,
    }));

    const addressFields: Array<InputFieldProps | InfoBoxProps | CustomRenderProps> = [
        {
            ariaLabel: fm('Email'),
            autoComplete: 'email',
            defaultValue: data.address.email || user?.email,
            label: fm('Email'),
            name: 'email',
            prefix: 'address',
            required: true,
            type: !!user?.email ? 'hidden' : 'email',
        },
        {
            $style: $styles.address.firstName,
            ariaLabel: fm('First name'),
            autoComplete: 'given-name',
            defaultValue: data.address.firstName || user?.firstname,
            label: fm('First name'),
            name: 'firstName',
            prefix: 'address',
            required: true,
            type: 'text',
            validate: 'name',
        },
        {
            $style: $styles.address.lastName,
            ariaLabel: fm('Last name'),
            autoComplete: 'family-name',
            defaultValue: data.address.lastName || user?.lastname,
            label: fm('Last name'),
            name: 'lastName',
            prefix: 'address',
            required: true,
            type: 'text',
            validate: 'name',
        },
        {
            $handleChange: event => handleSelectedCountry(event.target.value),
            addSearchFilter: true,
            ariaLabel: fm('Country'),
            autoComplete: 'address-level1',
            defaultValue: selectedCountry?.id ?? '',
            label: fm('Country'),
            name: 'country',
            options: countryOptions,
            prefix: 'address',
            required: true,
            type: 'select',
        },
        {
            ariaLabel: fm('State'),
            addSearchFilter: true,
            autoComplete: 'address-level1',
            defaultValue: selectedState,
            label: fm('State'),
            name: 'state',
            options: stateOptions,
            prefix: 'address',
            required: stateOptions.length > 0,
            type: 'select',
        },
        {
            boxes: infoBoxData,
            render: InfoBoxes,
            type: 'render',
        },
        {
            ariaLabel: fm('Address'),
            autoComplete: 'address-line1',
            defaultValue: data?.address?.address1 || user?.address || user?.addressLine1,
            label: fm('Address'),
            maxLength: 50,
            name: 'address1',
            prefix: 'address',
            required: true,
            type: 'text',
            validate: 'address',
        },
        {
            ariaLabel: fm('Address 2 (optional)'),
            autoComplete: 'address-line2',
            defaultValue: data?.address?.address2 || user?.address2 || user?.addressLine2,
            label: fm('Address 2 (optional)'),
            maxLength: 50,
            name: 'address2',
            prefix: 'address',
            required: false,
            type: 'text',
            validate: 'address',
        },
        {
            ariaLabel: fm('Zip code'),
            autoComplete: 'postal-code',
            dataLocale: selectedCountry?.id,
            defaultValue: data?.address?.zipCode || user?.postalCode,
            label: fm('Zip code'),
            name: 'zipCode',
            prefix: 'address',
            required: !zipCodeBlackList.includes(selectedCountry?.id),
            type: 'text',
            validate: 'zipCode',
        },
        {
            ariaLabel: fm('City'),
            autoComplete: 'address-level2',
            defaultValue: data?.address?.city || user?.city,
            label: fm('City'),
            maxLength: 30,
            name: 'city',
            prefix: 'address',
            required: true,
            type: 'text',
            validate: 'address',
        },
        {
            ariaLabel: fm('Phone number'),
            autoComplete: 'tel',
            defaultValue: parsedAddressPhoneNumberData.nationalNumber,
            label: fm('Phone number'),
            name: 'phoneNumber',
            prefix: 'address',
            required: true,
            type: 'tel',
            userCountry: parsedAddressPhoneNumberData.countryCode,
        },
        {
            $handleChange: event => checkboxData?.toggleSeparateShippingAndBilling(event),
            defaultValue: checkboxData?.separateBillingAndShippingAddress,
            label: checkboxData?.addressSeprateShippingText,
            name: 'seperateShippingAddress',
            required: false,
            type: 'checkbox',
        },
    ];

    const shippingAddressFields: Array<InputFieldProps | InfoBoxProps | CustomRenderProps> = [
        {
            $style: $styles.shippingAddress.shippingAddress,
            as: 'h3',
            children: fm('Shipping address'),
            fontKeys: 'Regular-16/100',
            render: Heading,
            type: 'render',
        },
        {
            ariaLabel: fm('Email'),
            autoComplete: 'email',
            defaultValue: data?.shippingAddress?.email,
            label: fm('Email'),
            name: 'email',
            prefix: 'shippingAddress',
            required: true,
            type: 'email',
        },
        {
            ariaLabel: fm('First name'),
            $style: $styles.shippingAddress.firstName,
            autoComplete: 'given-name',
            defaultValue: data?.shippingAddress?.firstName,
            label: fm('First name'),
            name: 'firstName',
            prefix: 'shippingAddress',
            required: true,
            type: 'text',
            validate: 'name',
        },
        {
            $style: $styles.shippingAddress.lastName,
            ariaLabel: fm('Last name'),
            autoComplete: 'family-name',
            defaultValue: data?.shippingAddress?.lastName,
            label: fm('Last name'),
            name: 'lastName',
            prefix: 'shippingAddress',
            required: true,
            type: 'text',
            validate: 'name',
        },
        {
            ariaLabel: fm('Country'),
            autoComplete: 'address-level1',
            defaultValue: selectedCountry?.id || null,
            label: fm('Country'),
            name: 'country',
            options: countryOptions,
            prefix: 'shippingAddress',
            required: true,
            type: 'select',
            validate: 'address',
        },
        {
            ariaLabel: fm('State'),
            autoComplete: 'address-level1',
            defaultValue: selectedState,
            label: fm('State'),
            name: 'state',
            options: stateOptions,
            prefix: 'shippingAddress',
            required: stateOptions.length > 0,
            type: 'select',
        },
        {
            boxes: infoBoxData,
            render: InfoBoxes,
            type: 'render',
        },
        {
            ariaLabel: fm('Address'),
            autoComplete: 'address-line1',
            defaultValue: data?.shippingAddress?.address1,
            label: fm('Address'),
            maxLength: 50,
            name: 'address1',
            prefix: 'shippingAddress',
            required: true,
            type: 'text',
            validate: 'address',
        },
        {
            ariaLabel: fm('Address 2 (optional)'),
            autoComplete: 'address-line2',
            defaultValue: data?.shippingAddress?.address2,
            label: fm('Address 2 (optional)'),
            maxLength: 50,
            name: 'address2',
            prefix: 'shippingAddress',
            required: false,
            type: 'text',
            validate: 'address',
        },
        {
            ariaLabel: fm('Zip code'),
            autoComplete: 'postal-code',
            dataLocale: selectedCountry?.id,
            defaultValue: data?.shippingAddress?.zipCode,
            label: fm('Zip code'),
            name: 'zipCode',
            prefix: 'shippingAddress',
            required: true,
            type: 'numeric',
            validate: 'zipCode',
        },
        {
            ariaLabel: fm('City'),
            autoComplete: 'address-level2',
            defaultValue: data?.shippingAddress?.city,
            label: fm('City'),
            maxLength: 30,
            name: 'city',
            prefix: 'shippingAddress',
            required: true,
            type: 'text',
            validate: 'address',
        },
        {
            ariaLabel: 'Phone number',
            autoComplete: 'tel',
            defaultValue: parsedShippingPhoneNumberData.nationalNumber,
            label: fm('Phone number'),
            name: 'phoneNumber',
            prefix: 'shippingAddress',
            required: true,
            type: 'tel',
            userCountry: parsedShippingPhoneNumberData.countryCode,
        },
    ];

    const finalCheckboxInputs: InputFieldProps[] = [
        {
            defaultValue: !!newsletterSignup,
            label: checkboxData?.addressNewsletterText,
            name: 'newsletter',
            required: false,
            type: 'checkbox',
        },
        {
            defaultValue: false,
            label: checkboxData?.addressTermsText,
            name: 'consent',
            required: true,
            type: 'checkbox',
        },
    ];

    let allFields = addressFields;

    // Add shipping address fields to the list of active fields if the user wants it
    if (checkboxData.separateBillingAndShippingAddress) {
        allFields = allFields.concat(shippingAddressFields);
    }

    // Add the final checkbox inputs after shippingAddress if it is added
    allFields = allFields.concat(finalCheckboxInputs);

    // Add prefix to field name to seperate shipping and billing allFields
    // The prefix will later be used to group fields before they are sent to Centra
    allFields = allFields.map(field => {
        if (field.type === 'render') {
            return field;
        }

        return {
            ...field,
            name: field.prefix ? `${field.prefix}_${field.name}` : field.name,
        };
    });

    return allFields;
};
